import React, { useState } from 'react';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';

interface FeedbackTipFeeSummaryProps {
  copy?: Record<string, string>;
  amountLabel: string;
  coverProcessingFee: boolean;
  onCoverProcessingFeeChange: (checked: boolean) => void;
  processingFeeLabel: string;
  technicianTipLabel: string;
  totalLabel: string;
}

const FeedbackTipFeeSummary: React.FC<FeedbackTipFeeSummaryProps> = ({
  copy,
  amountLabel,
  coverProcessingFee,
  onCoverProcessingFeeChange,
  processingFeeLabel,
  technicianTipLabel,
  totalLabel,
}) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Box
      sx={{
        bgcolor: 'var(--cx-brand-surface)',
        border: '1px solid',
        borderColor: 'var(--cx-brand-border)',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={coverProcessingFee}
              onChange={(event) => onCoverProcessingFeeChange(event.target.checked)}
              sx={{
                color: 'var(--cx-brand-text-muted)',
                '&.Mui-checked': { color: 'var(--cx-brand-text)' },
              }}
            />
          }
          label={
            <Typography
              variant="body2"
              sx={{ color: 'var(--cx-brand-text)', fontWeight: 600, fontSize: 14, lineHeight: '22px' }}
            >
              {copy?.['payment.coverFees'] ?? 'Cover payment & payout fees'}
            </Typography>
          }
          sx={{ m: 0, flex: 1, minWidth: 0, py: 0.5 }}
        />

        <IconButton
          aria-label={
            expanded
              ? (copy?.['payment.hideFees'] ?? 'Hide fee details')
              : (copy?.['payment.viewFees'] ?? 'View fee details')
          }
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
          sx={{ width: 44, height: 44, color: 'var(--cx-brand-text-muted)' }}
        >
          <ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : undefined }} />
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Divider sx={{ borderColor: 'var(--cx-brand-border)' }} />

        <Stack spacing={0.75} sx={{ p: 1.5 }}>
          {[
            [copy?.['payment.amount'] ?? 'Amount', amountLabel, 800],
            [copy?.['payment.fees'] ?? 'Payment & payout fees', processingFeeLabel, 800],
            [
              copy?.['payment.technicianReceives'] ?? 'Technician receives',
              technicianTipLabel,
              800,
            ],
            [copy?.['payment.total'] ?? 'Total', totalLabel, 900],
          ].map(([label, value, weight]) => (
            <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'var(--cx-brand-text-muted)',
                  fontWeight: weight === 900 ? 800 : undefined,
                }}
              >
                {label}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'var(--cx-brand-text)', fontWeight: weight }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
};

export default FeedbackTipFeeSummary;
